// Note lifecycle status and metadata (tasks/frontend-note-status-metadata-requirements.md).
//
// Current lifecycle: Draft → Active → Action Required → Inactive → Obsolete. Older notes (server or
// offline data not yet migrated) may still carry a legacy status: those keep rendering as-is, are
// styled like the status they map to, and are never offered as a new choice.

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const NOTE_STATUSES = ['Draft', 'Active', 'Action Required', 'Inactive', 'Obsolete'] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];

/** Status of a newly created note. */
export const DEFAULT_NOTE_STATUS: NoteStatus = 'Active';

export const NOTE_STATUS_DESCRIPTIONS: Record<NoteStatus, string> = {
  Draft: 'Still being created or developed',
  Active: 'Current, valid and intended for normal use',
  'Action Required': 'Current, but needs your attention',
  Inactive: 'Kept for history or reference; not current working information',
  Obsolete: 'No longer valid; should not be relied on',
};

/** Known legacy statuses and the current status they correspond to (display/styling hint only —
 * the backend migration does the real normalization). */
export const LEGACY_STATUS_MAP: Readonly<Record<string, NoteStatus>> = {
  'Next Action': 'Action Required',
  Completed: 'Inactive',
  Expired: 'Inactive',
  Cancelled: 'Obsolete',
};

export const NOTE_PRIORITIES = ['Low', 'Normal', 'High', 'Critical'] as const;
export type NotePriority = (typeof NOTE_PRIORITIES)[number];
export const DEFAULT_NOTE_PRIORITY: NotePriority = 'Normal';

export function isNoteStatus(value: unknown): value is NoteStatus {
  return typeof value === 'string' && (NOTE_STATUSES as readonly string[]).includes(value);
}

/** A non-empty status that isn't one of the current values (e.g. "Next Action", "Current"). */
export function isLegacyStatus(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '' && !isNoteStatus(value);
}

/** The current status a (possibly legacy) status stands for; unknown legacy values are returned as-is. */
export function effectiveStatus(status: string | null | undefined): string {
  const value = (status ?? '').trim();
  return LEGACY_STATUS_MAP[value] ?? value;
}

/** "Action Required" for a legacy "Next Action", null when there's no known mapping or it's current. */
export function legacyStatusHint(status: string | null | undefined): NoteStatus | null {
  return LEGACY_STATUS_MAP[(status ?? '').trim()] ?? null;
}

/** Grey + italic in lists: Inactive (and legacy Completed/Expired), and Obsolete. */
export function isMutedStatus(status: string | null | undefined): boolean {
  const s = effectiveStatus(status);
  return s === 'Inactive' || s === 'Obsolete';
}

/** Title strikethrough in lists: Obsolete (and legacy Cancelled). */
export function isStruckStatus(status: string | null | undefined): boolean {
  return effectiveStatus(status) === 'Obsolete';
}

/** Bold title in lists: Action Required (and legacy Next Action). */
export function isEmphasizedStatus(status: string | null | undefined): boolean {
  return effectiveStatus(status) === 'Action Required';
}

/** Statuses counted as "active" by the "All active" filter — current values plus the legacy ones
 * that meant the same. */
export const ACTIVE_FILTER_STATUSES: readonly string[] = [
  'Draft',
  'Active',
  'Action Required',
  'Current',
  'Inbox',
  'Next Action',
  'Someday',
];

export interface StatusOption {
  value: string;
  label: string;
  legacy: boolean;
  /** Tooltip: the status' meaning, or for a legacy status what it now corresponds to. */
  title: string;
}

/** Options for an editable status control: only the current statuses, plus — while the note still
 * has it — its legacy status, shown (not selectable) so the control displays the real value. */
export function statusOptionsFor(currentStatus: string | null | undefined): StatusOption[] {
  const options: StatusOption[] = NOTE_STATUSES.map((s) => ({ value: s, label: s, legacy: false, title: NOTE_STATUS_DESCRIPTIONS[s] }));
  if (isLegacyStatus(currentStatus)) {
    const value = String(currentStatus);
    const hint = legacyStatusHint(value);
    options.unshift({
      value,
      label: `${value} (old)`, // kept short: the dropdown sits in a single row on phones
      legacy: true,
      title: hint ? `Old status — now "${hint}". Pick a new status to update it.` : 'Old status. Pick a new status to update it.',
    });
  }
  return options;
}

export function isNotePriority(value: unknown): value is NotePriority {
  return typeof value === 'string' && (NOTE_PRIORITIES as readonly string[]).includes(value);
}

/** Missing/invalid priority reads as Normal. */
export function normalizePriority(value: unknown): NotePriority {
  return isNotePriority(value) ? value : DEFAULT_NOTE_PRIORITY;
}

const PRIORITY_RANK: Record<NotePriority, number> = { Low: 1, Normal: 2, High: 3, Critical: 4 };

export function priorityRank(value: unknown): number {
  return PRIORITY_RANK[normalizePriority(value)];
}

/** Whether the note needs attention: its flag, or an (old or current) Action Required status. */
export function isActionRequired(note: { actionRequired?: unknown; Status?: string | null } | null | undefined): boolean {
  if (!note) return false;
  return note.actionRequired === true || effectiveStatus(note.Status) === 'Action Required';
}

export interface PriorityIndicator {
  /** PrimeIcons class; null for Normal (no indicator). */
  icon: string | null;
  label: string;
  colorClass: string;
}

export function priorityIndicator(value: unknown): PriorityIndicator {
  const priority = normalizePriority(value);
  switch (priority) {
    case 'Low':
      return { icon: 'pi pi-angle-double-down', label: 'Low priority', colorClass: 'text-sky-600 dark:text-sky-400' };
    case 'High':
      return { icon: 'pi pi-angle-double-up', label: 'High priority', colorClass: 'text-orange-600 dark:text-orange-400' };
    case 'Critical':
      return { icon: 'pi pi-exclamation-circle', label: 'Critical priority', colorClass: 'text-red-600 dark:text-red-400' };
    default:
      return { icon: null, label: 'Normal priority', colorClass: '' };
  }
}

/** A stored date/time is usable when absent, or a string/Date that parses. */
export function isValidOptionalDate(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  const time = value instanceof Date ? value.getTime() : typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isFinite(time);
}

/** ISO string for a picker/stored value; null when empty or not a valid date. */
export function toOptionalIsoDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

/** Picker value (Date) for a stored date/time; null when empty or invalid. */
export function toOptionalDate(value: unknown): Date | null {
  const iso = toOptionalIsoDate(value);
  return iso ? new Date(iso) : null;
}

/** Status control validator: a current status, or the note's own legacy status (shown until the user
 * picks a new one — never blocks editing a note that still has it). */
export function noteStatusValidator(originalStatus: string | null | undefined): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null; // Validators.required reports it
    if (isNoteStatus(value) || value === originalStatus) return null;
    return { noteStatus: true };
  };
}

export const notePriorityValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  control.value === null || control.value === undefined || control.value === '' || isNotePriority(control.value)
    ? null
    : { notePriority: true };

export const optionalDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  isValidOptionalDate(control.value) ? null : { invalidDate: true };
